import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TOPICS = ['Generative AI', 'Agentic AI', 'RAG', 'LLMs', 'Machine Learning'];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const LessonEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = id !== 'new';

  const [form, setForm] = useState({
    title: '',
    slug: '',
    topic: 'Generative AI',
    summary: '',
    content: '',
    analogy: '',
    tryThis: '',
    readTime: '3 min read',
    date: new Date().toISOString().split('T')[0],
    published: true,
    featured: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const headers = () => {
    const t = localStorage.getItem('admin_token');
    if (!t) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${t}` };
  };

  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      setLoading(true);
      try {
        const h = headers();
        if (!h) return;
        const res = await axios.get(`${BACKEND_URL}/api/admin/lessons`, { headers: h });
        const l = res.data.find(x => x.id === id);
        if (!l) { toast.error('Lesson not found'); navigate('/admin/lessons'); return; }
        setForm({
          title: l.title,
          slug: l.slug,
          topic: l.topic,
          summary: l.summary,
          content: l.content,
          analogy: l.analogy || '',
          tryThis: l.tryThis || '',
          readTime: l.readTime,
          date: l.date,
          published: l.published,
          featured: l.featured,
        });
      } catch {
        toast.error('Failed to load lesson');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const update = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'title' && !isEdit) {
      setForm(prev => ({ ...prev, slug: slugify(value) }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const h = headers();
      if (!h) return;
      const payload = { ...form };
      if (isEdit) {
        await axios.put(`${BACKEND_URL}/api/admin/lessons/${id}`, payload, { headers: h });
        toast.success('Lesson updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/lessons`, payload, { headers: h });
        toast.success('Lesson created');
      }
      navigate('/admin/lessons');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="text-gray-600">Loading...</p></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/admin/lessons" className="flex items-center gap-2 text-gray-600 hover:text-black">
            <ArrowLeft size={20} /><span>Back to Lessons</span>
          </Link>
          <h1 className="text-2xl font-light">{isEdit ? 'Edit Lesson' : 'New Lesson'}</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <form onSubmit={submit} className="bg-white rounded-lg border border-gray-200 p-8 space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Title *</label>
            <input required value={form.title} onChange={e => update('title', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-md" data-testid="lesson-title-input" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Slug *</label>
            <input required value={form.slug} onChange={e => update('slug', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-md" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Topic *</label>
              <select value={form.topic} onChange={e => update('topic', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md" data-testid="lesson-topic-select">
                {TOPICS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Date *</label>
              <input type="date" required value={form.date} onChange={e => update('date', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Summary *</label>
            <textarea required rows={2} value={form.summary} onChange={e => update('summary', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-md" placeholder="1-2 sentence hook" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Content * (use ## for headings)</label>
            <textarea required rows={14} value={form.content} onChange={e => update('content', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-md font-mono text-sm" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Analogy (teen-relatable example)</label>
            <textarea rows={3} value={form.analogy} onChange={e => update('analogy', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-md" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Try This (mini-exercise)</label>
            <textarea rows={2} value={form.tryThis} onChange={e => update('tryThis', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-md" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Read Time</label>
              <input value={form.readTime} onChange={e => update('readTime', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md" placeholder="3 min read" />
            </div>
            <div className="flex items-end gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.published} onChange={e => update('published', e.target.checked)} className="w-4 h-4" />
                <span className="text-sm">Publish</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={e => update('featured', e.target.checked)} className="w-4 h-4" />
                <span className="text-sm">Featured</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-6 border-t">
            <Link to="/admin/lessons" className="px-6 py-3 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</Link>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
              data-testid="lesson-save-btn">
              <Save size={20} />
              <span>{saving ? 'Saving...' : isEdit ? 'Update Lesson' : 'Create Lesson'}</span>
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default LessonEditor;
